import { ref, readonly } from "vue";

const toasts = ref([]);
let toastId = 0;

const defaultDuration = 5000;

/**
 * Remove a toast by ID
 * @param {number} id - Toast ID to remove
 */
function removeToast(id) {
  const index = toasts.value.findIndex((t) => t.id === id);
  if (index > -1) {
    toasts.value.splice(index, 1);
  }
}

const icons = {
  success: "CheckCircleIcon",
  error: "XCircleIcon",
  warning: "ExclamationTriangleIcon",
  info: "InformationCircleIcon",
};

const iconColors = {
  success: "text-green-400",
  error: "text-red-400",
  warning: "text-yellow-400",
  info: "text-blue-400",
};

/**
 * Add a new toast notification
 * @param {Object|string} options - Toast options or message string
 * @param {string} options.message - The message to display
 * @param {string} options.type - Type of toast: 'success', 'error', 'warning', 'info'
 * @param {number} options.duration - Duration in ms before auto-dismiss (0 = no auto-dismiss)
 * @returns {number} Toast ID
 */
function addToast(options) {
  toastId += 1;
  const id = toastId;

  let toast;
  if (typeof options === "string") {
    toast = {
      id,
      message: options,
      type: "info",
      duration: defaultDuration,
    };
  } else {
    toast = {
      id,
      message: options.message,
      type: options.type || "info",
      duration: options.duration ?? defaultDuration,
    };
  }

  toast.icon = icons[toast.type] || icons.info;
  toast.iconColor = iconColors[toast.type] || iconColors.info;

  toasts.value.push(toast);

  if (toast.duration > 0) {
    setTimeout(() => {
      removeToast(id);
    }, toast.duration);
  }

  return id;
}

/**
 * Remove all toasts
 */
function clearToasts() {
  toasts.value = [];
}

// Convenience methods
function success(message, duration) {
  return addToast({ message, type: "success", duration });
}

function error(message, duration) {
  return addToast({ message, type: "error", duration });
}

function warning(message, duration) {
  return addToast({ message, type: "warning", duration });
}

function info(message, duration) {
  return addToast({ message, type: "info", duration });
}

/**
 * Legacy compatibility: open() method for vue-toast-notification compatibility
 * @param {Object} options - Toast options
 * @param {string} options.message - The message to display
 * @param {string} options.type - Type of toast
 * @param {number} options.duration - Duration in ms
 */
function open(options) {
  return addToast(options);
}

export function useToast() {
  return {
    toasts: readonly(toasts),
    addToast,
    removeToast,
    clearToasts,
    success,
    error,
    warning,
    info,
    open,
  };
}

// Export for Vue plugin compatibility (this.$toast)
export const toastPlugin = {
  install(vueApp) {
    const toast = useToast();
    // eslint-disable-next-line no-param-reassign
    vueApp.config.globalProperties.$toast = toast;
    vueApp.provide("toast", toast);
  },
};

export default useToast;
